/**
 * Query keys de faturas avulsas (`student_invoices`) — lista da página
 * /faturas e totais agregados dos cards.
 *
 * O root preserva o prefixo legado `['student-invoices']` de propósito:
 * as mutações existentes (markPaid em useStudentInvoices, useChargeInvoiceLink,
 * useSelfSignupLeads, Opportunities) invalidam por esse prefixo — mudar o
 * root quebraria a invalidation silenciosamente (prefix-match).
 */
import { tuple } from "./core";

const root = ["student-invoices"] as const;

export interface StudentInvoiceListFilters {
  clubId: string | null;
  status: string | null;
  /** Competência 'yyyy-MM' sobre due_date. */
  competencia: string | null;
  search: string;
  /** Situação do aviso ao aluno (`situacaoDoAviso`, lib/invoice-notice-status do CRM). */
  aviso?: string | null;
  /** Envio pela recepção (`envioDaFatura`). */
  envio?: string | null;
}

export const invoices = {
  /** Prefixo raiz — igual ao legado; mutações invalidam isto. */
  all: root,
  /** Lista paginada/filtrada da página /faturas. */
  list: (
    clubId: string | null | undefined,
    viewScope: unknown,
    scopedTeacherId: string | null | undefined,
    filters: StudentInvoiceListFilters | null,
    page: number,
    pageSize: number,
  ) => tuple(root, "list", clubId, viewScope, scopedTeacherId, filters, page, pageSize),
  /** Totais dos cards (query agregada, sem paginação/status/search). */
  totals: (
    clubId: string | null | undefined,
    viewScope: unknown,
    scopedTeacherId: string | null | undefined,
    clubFilter: string | null,
  ) => tuple(root, "totals", clubId, viewScope, scopedTeacherId, clubFilter),
  /**
   * Prévia da troca de meio (RPC alterar_meio_de_cobranca com p_simular).
   * Raiz PRÓPRIA, fora de student-invoices de propósito: a execução invalida
   * qk.invoices.all, e uma prévia sob essa raiz se refaria em silêncio logo
   * depois de executar, mostrando outro resultado que o do que foi feito.
   */
  previaDaTroca: (pedido: unknown) => tuple(["troca-de-meio"] as const, "previa", pedido),
  /**
   * UMA fatura (detalhe lateral). Sob `student-invoices`: baixa, cobrança e
   * "marcar como enviado" já invalidam `qk.invoices.all` e o detalhe acompanha.
   */
  detail: (invoiceId: string | null | undefined) => tuple(root, "detail", invoiceId),
  /** Linha do tempo da fatura (RPC `get_invoice_timeline`). */
  timeline: (invoiceId: string | null | undefined) => tuple(root, "timeline", invoiceId),
  /**
   * Histórico de alterações do RECORTE de /faturas (RPC eventos_do_recorte,
   * 20261005160300). `args` = argumentosDoRecorte sem `p_hoje` (+ professor).
   * Sob o root `student-invoices`: toda mutação de fatura (baixa, valor, troca
   * de meio) já invalida.
   */
  historicoDoRecorte: (args: unknown, page: number, pageSize: number) =>
    tuple(root, "historico", "recorte", args, page, pageSize),
  /**
   * Contexto do gerador mensal para o "Alterar valor" (`aguardando_mensalidade`,
   * lib/valor-da-fatura do CRM): os grupos (dia, próximo ciclo) do gerador por
   * aluno das faturas candidatas (RPC ciclos_do_gerador, 20261006120000). Sob
   * `student-invoices`: mudar valor ou gerar a mensalidade invalida junto com a
   * lista. `clubId` = o clube consultado (só o ativo, e só com invoices.manage).
   */
  ciclosDoGerador: (clubId: string | null, alunos: readonly string[]) =>
    tuple(root, "ciclos-do-gerador", clubId, alunos),
  /**
   * Configuração de notificações do customer no Asaas (edge
   * `asaas-customer-notifications`). Raiz PRÓPRIA, fora de `student-invoices`:
   * é uma chamada externa, só leitura, e não deve refazer a cada baixa ou
   * envio registrado na lista.
   */
  asaasNotifications: (invoiceId: string | null | undefined) =>
    tuple(["asaas-customer-notifications"] as const, invoiceId),
} as const;
